import { Connection, FlowNode, Schemes } from '@features/flow-ui-creator/interfaces/flow-ui.interface';
import { NodeType } from '@features/flow-ui-creator/interfaces/node.interface';
import { CurveFactoryLineOnly, line } from 'd3-shape';
import { ClassicPreset, GetSchemes, NodeEditor } from 'rete';
import { Position } from '@core/interfaces/position.interface';

interface Cloneable {
  clone(keepId?: boolean): this;
}

type ClassicNode = ClassicPreset.Node &
  FlowNode & { type: string; data: Record<string, any>; parent?: string } & Cloneable;
type C<S extends ClassicNode, T extends ClassicNode> = ClassicPreset.Connection<S, T> & { isLoop?: boolean };
export type ClassicSchemes = GetSchemes<ClassicNode, C<ClassicNode, ClassicNode>>;

export class PathFactory {
  constructor(private curve: CurveFactoryLineOnly) {}

  getData(points: Position[]): string {
    const getPath = line()
      .x((dimension) => {
        return dimension[0];
      })
      .y((dimension) => {
        return dimension[1];
      })
      .curve(this.curve);

    return getPath(
      points.map((point) => {
        return [point.x, point.y];
      }),
    );
  }
}

export function areConnected<S extends ClassicSchemes | Schemes>(
  editor: NodeEditor<S>,
  source: string,
  target: string,
  loop = false,
  cache = new Set<string>(),
): boolean {
  const list = editor
    .getConnections()
    .filter((connection) => {
      return connection.source === source && !connection.isLoop;
    })
    .map((connection) => {
      return editor.getNode(connection.target);
    });
  const current = editor.getNode(source);
  const targetNode = editor.getNode(target);

  if (!current) {
    throw new Error(`Node ${source} not found`);
  }

  const currentParent = (current as ClassicNode).parent;

  if (source === target) {
    return true;
  }

  if (cache.has(source)) {
    return false;
  }

  cache.add(source);

  if (loop && targetNode.nodeData.type.toLowerCase() === NodeType.Loop.toLowerCase()) {
    return true;
  }

  for (const node of list) {
    if (node.id === target) {
      return true;
    }

    if (areConnected(editor, node.id, target, loop, cache)) {
      return true;
    }
  }

  if (currentParent && areConnected(editor, currentParent, target, loop, cache)) {
    return true;
  }

  return false;
}

export function disableAccumulateOnCtrl(): {
  active(): boolean;
  destroy(): void;
} {
  let pressed = false;

  function keydown(e: KeyboardEvent) {
    if (e.key === 'Control' || e.key === 'Meta') {
      pressed = true;
    }
  }

  function keyup(e: KeyboardEvent) {
    if (e.key === 'Control' || e.key === 'Meta') {
      pressed = false;
    }
  }

  document.addEventListener('keydown', keydown);
  document.addEventListener('keyup', keyup);

  return {
    active() {
      return false;
    },
    destroy() {
      document.removeEventListener('keydown', keydown);
      document.removeEventListener('keyup', keyup);
    },
  };
}

export function detectLoopConnections(
  connections: {
    from: string;
    to: string;
    connection: Connection<FlowNode>;
  }[],
): void {
  const adjacencyNodes: Record<string, string[]> = {};
  const loops: Set<string> = new Set();
  const traverse = (node: string, visited: Set<string>, path: string[]): void => {
    if (visited.has(node)) {
      const loopStart = path.indexOf(node);

      if (loopStart !== -1) {
        for (let i = loopStart; i < path.length - 1; i++) {
          loops.add(`${path[i]}**${path[i + 1]}`);
        }
      }

      return;
    }

    visited.add(node);
    path.push(node);

    for (const neighbor of adjacencyNodes[node] || []) {
      traverse(neighbor, new Set(visited), [...path]);
    }
  };

  for (const { from, to } of connections) {
    if (!adjacencyNodes[from]) adjacencyNodes[from] = [];
    adjacencyNodes[from].push(to);
  }

  Object.keys(adjacencyNodes).forEach((node) => {
    traverse(node, new Set(), []);
  });

  // Mark connections as isLoop
  for (const { from, to, connection } of connections) {
    const key = `${from}**${to}`;

    if (loops.has(key)) {
      connection.isLoop = true;
    } else {
      delete connection.isLoop;
    }
  }
}
